import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { GoogleIdentityService } from 'src/app/services/identity.service';
import { AdvancedDebugging, isExpectedFirebaseAuthUserError } from 'src/app/utilities';

const OP_WATCH_USER = 'dashboard:DashboardSignInFacade.watchUser';
const OP_SIGN_IN = 'dashboard:DashboardSignInFacade.signInWithGoogle';
const OP_NAVIGATE = 'dashboard:DashboardSignInFacade.navigate';

/** Dashboard sign-in (Google); scoped to {@link SignInComponent} via `providers`. */
@Injectable()
export class DashboardSignInFacade {
	readonly identityService = inject(GoogleIdentityService);
	private readonly router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);
	private readonly advancedDebugging = new AdvancedDebugging();

	constructor() {
		this.identityService.getUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: user => {
				if (user) {
					void this.router.navigate(['dashboard']).catch(error =>
						this.advancedDebugging.captureError(OP_NAVIGATE, error, {
							tags: { operation: OP_NAVIGATE, app: 'dashboard' },
						}),
					);
				}
			},
			error: error =>
				this.advancedDebugging.captureError(OP_WATCH_USER, error, {
					tags: { operation: OP_WATCH_USER, app: 'dashboard' },
				}),
		});
	}

	async signInWithGoogle(): Promise<void> {
		try {
			await this.identityService.signInPopup();
		} catch (error) {
			if (!isExpectedFirebaseAuthUserError(error)) {
				this.advancedDebugging.captureError(OP_SIGN_IN, error, {
					tags: { operation: OP_SIGN_IN, app: 'dashboard' },
				});
			}
			throw error;
		}
		try {
			await this.router.navigate(['dashboard']);
		} catch (error) {
			this.advancedDebugging.captureError(OP_NAVIGATE, error, {
				tags: { operation: OP_NAVIGATE, app: 'dashboard' },
			});
			throw error;
		}
	}
}
